import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import {
  Box, Stack, Typography, Button, Alert, Chip, Tabs, Tab, CircularProgress, Breadcrumbs, Link,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import CheckIcon from "@mui/icons-material/Check";
import { BRAND } from "../styles/theme";
import {
  Collector, CollectorConfig, ConfigCatalog, collectorFromConfig, getCollectorConfig, getConfigCatalog,
  setConfigValue, downloadContractPdf, sendCollectorsReport,
} from "../services/collectors";
import { ProcessorChip, BoolChip } from "../components/collectors/chips";
import ConfirmDialog from "../components/collectors/ConfirmDialog";
import OverviewTab from "../components/collectors/OverviewTab";
import ConfigTab from "../components/collectors/ConfigTab";
import AccountsTab from "../components/collectors/AccountsTab";
import UsersTab from "../components/collectors/UsersTab";
import OnboardingTab from "../components/collectors/OnboardingTab";

const TABS = ["overview", "config", "accounts", "users", "onboarding"] as const;
type TabKey = typeof TABS[number];

/** Contexto que comparten las tabs: el collector, su config y cómo refrescarla. */
export interface CollectorCtx {
  collector: Collector;
  config:    CollectorConfig;
  catalog:   ConfigCatalog;
  reload:    () => Promise<void>;
  notify:    (msg: string, severity?: "success" | "error") => void;
}

/**
 * Detalle de un collector: un solo header (id, procesador, estado, close) y
 * cinco tabs ruteadas (/collector/:id/<tab>). Reemplaza las pantallas cruzadas
 * Config/Accounts/Users/History/Onboarding de v1.
 */
export default function CollectorDetail() {
  const { id = "", tab: tabParam } = useParams();
  const tab: TabKey = (TABS as readonly string[]).includes(tabParam ?? "") ? (tabParam as TabKey) : "overview";
  const nav = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();
  const isNew = sp.get("new") === "1";

  const [collector, setCollector] = useState<Collector | null>(null);
  const [config, setConfig]       = useState<CollectorConfig>({});
  const [catalog, setCatalog]     = useState<ConfigCatalog>({});
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState<string | null>(null);
  const [toast, setToast]         = useState<{ msg: string; severity: "success" | "error" } | null>(null);
  const [confirmActive, setConfirmActive] = useState(false);
  const [reportOpen, setReportOpen]       = useState(false);

  const reload = useCallback(async () => {
    setErr(null);
    try {
      const [cfg, cat] = await Promise.all([getCollectorConfig(id), getConfigCatalog()]);
      const c = collectorFromConfig(id, cfg);   // CP-445: sin traer la lista completa
      if (!c) { setCollector(null); setErr(`Collector “${id}” not found.`); return; }
      setCollector(c); setConfig(cfg); setCatalog(cat);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { setLoading(true); void reload(); }, [reload]);

  const notify = useCallback((msg: string, severity: "success" | "error" = "success") => {
    setToast({ msg, severity });
    window.setTimeout(() => setToast((t) => (t?.msg === msg ? null : t)), 5000);
  }, []);

  // Mismo criterio que el filtro del backend (GET /collectors?active=true compara
  // el value_str tal cual): "true" es activo; cualquier otra cosa, inactivo.
  const active = useMemo(() => (config.active ?? "true").toString().toLowerCase() === "true", [config]);

  /** El valor a escribir tiene que pasar el regex del catálogo; "true"/"false" es lo que compara el backend. */
  function activeValue(on: boolean): string {
    const re = catalog.active?.validation;
    const candidates = on ? ["true", "Yes", "1"] : ["false", "No", "0"];
    if (!re) return candidates[0];
    try { const r = new RegExp(re); return candidates.find((c) => r.test(c)) ?? candidates[0]; } catch { return candidates[0]; }
  }

  async function toggleActive() {
    if (!collector) return;
    await setConfigValue(collector, "active", activeValue(!active));
    setConfirmActive(false);
    notify(active ? "Collector deactivated" : "Collector activated");
    await reload();
  }

  async function onContract() {
    if (!collector) return;
    try { await downloadContractPdf(collector.id); }
    catch (e: any) { notify(String(e?.message ?? e), "error"); }
  }

  async function onReport() {
    if (!collector) return;
    await sendCollectorsReport(collector.id); setReportOpen(false); notify("Report queued — it will arrive by email in a few minutes.");
  }

  const go = (t: TabKey) => nav(`/collector/${encodeURIComponent(id)}${t === "overview" ? "" : `/${t}`}`);

  if (loading) {
    return <Box sx={{ p: 3, display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  }
  if (!collector) {
    return (
      <Box sx={{ p: 3 }}>
        <Button component={RouterLink} to="/collector" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>Back to collectors</Button>
        <Alert severity="error">{err ?? `Collector “${id}” not found.`}</Alert>
      </Box>
    );
  }

  const ctx: CollectorCtx = { collector, config, catalog, reload, notify };

  return (
    <Box sx={{ p: 3 }}>
      <Button component={RouterLink} to="/collector" startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 1 }}>
        Back to collectors
      </Button>
      <Breadcrumbs sx={{ mb: 1, fontSize: 13 }}>
        <Link component={RouterLink} to="/collector" underline="hover" color="text.secondary">Collectors</Link>
        <Typography variant="body2" color="text.primary">{collector.id}</Typography>
      </Breadcrumbs>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{collector.id}</Typography>
            <ProcessorChip value={collector.paymentsProcessor} />
            <BoolChip value={active} yes="Active" no="Inactive" />
            {config.close_id
              ? <Chip size="small" variant="outlined" label={`Close ${config.close_id}`} sx={{ height: 22, fontSize: 11, fontWeight: 500 }} />
              : <Chip size="small" label="No close_id" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#FFF6E0", color: "#9A6700" }} />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {config.company_name || "—"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={onContract}
            sx={{ borderColor: BRAND.primary, color: BRAND.primary }}>Contract PDF</Button>
          <Button size="small" variant="outlined" startIcon={<MailOutlineIcon />} onClick={() => setReportOpen(true)}
            sx={{ borderColor: BRAND.primary, color: BRAND.primary }}>Status report</Button>
          <Button size="small" variant={active ? "text" : "contained"} color={active ? "primary" : "primary"}
            startIcon={active ? <PowerSettingsNewIcon /> : <CheckIcon />} onClick={() => setConfirmActive(true)}>
            {active ? "Deactivate" : "Activate"}
          </Button>
        </Stack>
      </Stack>

      {isNew && tab === "config" && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => nav(location.pathname, { replace: true })}>
          <b>Collector created.</b> Add its configuration below — at least <code>company_name</code>, <code>charge_rate</code> and <code>create_accounts</code>.
        </Alert>
      )}
      {err   && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {toast && <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ mb: 2 }}>{toast.msg}</Alert>}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2.5 }}>
        <Tabs value={tab} onChange={(_, v) => go(v)} textColor="primary" indicatorColor="primary">
          {([
            ["overview", "Overview", null],
            ["config", "Config", Object.keys(config).length],
            ["accounts", "Accounts", null],
            ["users", "Users", null],
            ["onboarding", "Onboarding & contract", null],
          ] as [TabKey, string, number | null][]).map(([k, label, n]) => (
            <Tab key={k} value={k} sx={{ textTransform: "none", fontWeight: 600, fontSize: 15, minHeight: 48 }}
              label={<Stack direction="row" spacing={1} alignItems="center"><span>{label}</span>
                {n != null && <Chip size="small" label={n} sx={{ height: 18, fontSize: 11, fontWeight: 700, bgcolor: tab === k ? BRAND.pendingBg : "#EEE", color: tab === k ? BRAND.primary : "#666" }} />}
              </Stack>} />
          ))}
        </Tabs>
      </Box>

      {tab === "overview"   && <OverviewTab ctx={ctx} onGo={go} />}
      {tab === "config"     && <ConfigTab ctx={ctx} />}
      {tab === "accounts"   && <AccountsTab ctx={ctx} />}
      {tab === "users"      && <UsersTab ctx={ctx} />}
      {tab === "onboarding" && <OnboardingTab ctx={ctx} />}

      <ConfirmDialog open={confirmActive} onClose={() => setConfirmActive(false)} onConfirm={toggleActive}
        title={active ? "Deactivate collector" : "Activate collector"} danger={active}
        okLabel={active ? "Deactivate" : "Activate"} busyLabel={active ? "Deactivating…" : "Activating…"}>
        {active
          ? <><b>{collector.id}</b> will stop being reported and its vendor web users won't be able to log in. Existing accounts keep receiving payments. Sets <code>active = false</code>.</>
          : <><b>{collector.id}</b> will be reported again and its users can log in. Sets <code>active = true</code>.</>}
      </ConfirmDialog>

      <ConfirmDialog open={reportOpen} onClose={() => setReportOpen(false)} onConfirm={onReport}
        title="Send account status report" okLabel="Send report" busyLabel="Sending…">
        This emails the account status summary for <b>{collector.id}</b> to <b>operaciones@cucuru.com</b>. It can take a few minutes to arrive.
      </ConfirmDialog>
    </Box>
  );
}
